import Papa from 'papaparse';
import { v4 as uuidv4 } from 'uuid';
import type { ParsedDataset } from '../types/visualizations';

/**
 * Parse CSV content into a structured dataset
 */
export function parseCSV(content: string, filename: string): ParsedDataset | null {
  try {
    const result = Papa.parse(content, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim(),
    });

    if (result.errors.length > 0) {
      console.warn('CSV parsing had errors:', result.errors);
    }

    if (!result.data || result.data.length === 0) {
      return null;
    }

    const headers = result.meta.fields || [];
    const rows = result.data as Array<Record<string, string | number>>;

    return {
      id: uuidv4(),
      name: filename.replace(/\.[^/.]+$/, ''), // Remove extension
      headers,
      rows,
    };
  } catch (error) {
    console.error('Failed to parse CSV:', error);
    return null;
  }
}

/**
 * Parse JSON content into a structured dataset
 * Handles both array of objects and { data: [...] } format
 */
export function parseJSON(content: string, filename: string): ParsedDataset | null {
  try {
    const parsed = JSON.parse(content);

    // Handle different JSON structures
    let dataArray: Array<Record<string, unknown>>;

    if (Array.isArray(parsed)) {
      dataArray = parsed;
    } else if (parsed.data && Array.isArray(parsed.data)) {
      dataArray = parsed.data;
    } else if (parsed.rows && Array.isArray(parsed.rows)) {
      dataArray = parsed.rows;
    } else if (parsed.results && Array.isArray(parsed.results)) {
      dataArray = parsed.results;
    } else if (typeof parsed === 'object' && parsed !== null) {
      // Try to convert single object to array
      dataArray = [parsed];
    } else {
      return null;
    }

    if (dataArray.length === 0) {
      return null;
    }

    // Extract headers from first object
    const headers = Object.keys(dataArray[0]);

    // Convert values to string | number
    const rows = dataArray.map((row) => {
      const converted: Record<string, string | number> = {};
      for (const key of headers) {
        const value = row[key];
        if (typeof value === 'number') {
          converted[key] = value;
        } else if (typeof value === 'string') {
          // Try to parse as number
          const num = parseFloat(value);
          converted[key] = !isNaN(num) && isFinite(num) ? num : value;
        } else if (value === null || value === undefined) {
          converted[key] = '';
        } else {
          converted[key] = String(value);
        }
      }
      return converted;
    });

    return {
      id: uuidv4(),
      name: filename.replace(/\.[^/.]+$/, ''),
      headers,
      rows,
    };
  } catch (error) {
    console.error('Failed to parse JSON:', error);
    return null;
  }
}

/**
 * Auto-detect file type and parse accordingly
 */
export function parseDataFile(content: string, filename: string): ParsedDataset | null {
  const extension = filename.toLowerCase().split('.').pop();

  switch (extension) {
    case 'csv':
    case 'tsv':
      return parseCSV(content, filename);
    case 'json':
      return parseJSON(content, filename);
    default:
      // Try JSON first, then CSV
      const jsonResult = parseJSON(content, filename);
      if (jsonResult) return jsonResult;
      return parseCSV(content, filename);
  }
}

/**
 * Get numeric columns from a dataset
 */
export function getNumericColumns(dataset: ParsedDataset): string[] {
  if (dataset.rows.length === 0) return [];

  return dataset.headers.filter((header) => {
    // Check first few rows to determine if column is numeric
    const sampleRows = dataset.rows.slice(0, Math.min(10, dataset.rows.length));
    return sampleRows.every((row) => {
      const value = row[header];
      return typeof value === 'number' || (typeof value === 'string' && !isNaN(parseFloat(value)));
    });
  });
}

/**
 * Get categorical columns from a dataset (non-numeric)
 */
export function getCategoricalColumns(dataset: ParsedDataset): string[] {
  const numericCols = new Set(getNumericColumns(dataset));
  return dataset.headers.filter((header) => !numericCols.has(header));
}

/**
 * Check if a file is a data file (CSV or JSON)
 */
export function isDataFile(filename: string): boolean {
  const extension = filename.toLowerCase().split('.').pop();
  return ['csv', 'tsv', 'json'].includes(extension || '');
}
